import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import {Crown, Loader2, MessageCircleDashed as MessageCircle, Send, X} from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '@/lib/lumi'
import type { ArticleComment, NewsArticle } from '@/types'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const COMMENT_DRAFT_KEY = 'cf-comment-draft'

interface CommentThreadProps {
  article: NewsArticle
  open?: boolean
  onOpenChange?: (open: boolean) => void
  onCountChange?: (count: number) => void
}

function formatCommentDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return ''
  }
}

function initials(name: string) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

export default function CommentThread({
  article,
  open: openProp,
  onOpenChange,
  onCountChange,
}: CommentThreadProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  const [approved, setApproved] = useState<ArticleComment[]>([])
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [content, setContent] = useState('')

  const open = openProp ?? internalOpen
  const setOpen = onOpenChange ?? setInternalOpen

  const loadApproved = useCallback(async () => {
    if (!article?._id) return
    try {
      const { list } = await lumi.entities.article_comments.list({
        filter: { articleId: article._id },
        sort: { createdAt: -1 },
        limit: 200,
      })
      const items = ((list || []) as ArticleComment[])
        .filter((item) => item?.status === 'approved')
        .slice()
        .sort((a, b) => {
          if (Boolean(a.isPinned) === Boolean(b.isPinned)) {
            return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
          }
          return a.isPinned ? -1 : 1
        })
      setApproved(items)
      setCount(items.length)
      onCountChange?.(items.length)
    } catch (e) {
      console.error('Erro ao carregar comentários:', e)
    }
  }, [article?._id, onCountChange])

  useEffect(() => {
    loadApproved()
  }, [loadApproved])

  useEffect(() => {
    try {
      const raw = localStorage.getItem(COMMENT_DRAFT_KEY)
      if (!raw) return
      const draft = JSON.parse(raw) as { name?: string; email?: string }
      if (draft?.name) setName(draft.name)
      if (draft?.email) setEmail(draft.email)
    } catch {
      /* silencioso */
    }
  }, [])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
    }
  }, [open, setOpen])

  const openModal = async (e?: React.MouseEvent) => {
    e?.stopPropagation()
    e?.preventDefault()
    setOpen(true)
    setLoading(true)
    await loadApproved()
    setLoading(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmedName = name.trim()
    const trimmedEmail = email.trim().toLowerCase()
    const trimmedContent = content.trim()

    if (trimmedName.length < 2) {
      toast.error('Indique o seu nome.')
      return
    }
    if (!EMAIL_RE.test(trimmedEmail)) {
      toast.error('Indique um e-mail válido.')
      return
    }
    if (trimmedContent.length < 4) {
      toast.error('Escreva um comentário com pelo menos 4 caracteres.')
      return
    }
    if (trimmedContent.length > 800) {
      toast.error('O comentário pode ter no máximo 800 caracteres.')
      return
    }

    setSubmitting(true)
    const now = new Date().toISOString()
    try {
      await lumi.entities.article_comments.create({
        articleId: article._id,
        articleTitle: article.title || '',
        articleAuthorId: article.authorId || '',
        authorName: trimmedName,
        authorEmail: trimmedEmail,
        content: trimmedContent,
        status: 'pending',
        isPinned: false,
        creator: trimmedEmail,
        createdAt: now,
        updatedAt: now,
      })
      try {
        localStorage.setItem(COMMENT_DRAFT_KEY, JSON.stringify({ name: trimmedName, email: trimmedEmail }))
      } catch {
        /* silencioso */
      }
      setContent('')
      toast.success('Comentário enviado. Fica visível após aprovação.')
    } catch (err) {
      console.error('Erro ao enviar comentário:', err)
      toast.error('Não foi possível enviar o comentário. Tente novamente.')
    } finally {
      setSubmitting(false)
    }
  }

  const modal = open ? (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/70 px-0 sm:px-4"
      onClick={() => setOpen(false)}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`comments-title-${article._id}`}
    >
      <div
        className="w-full sm:max-w-xl max-h-[92vh] bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden flex flex-col animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-950/50 flex items-center justify-center shrink-0">
            <MessageCircle size={18} className="text-red-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 id={`comments-title-${article._id}`} className="text-base font-bold text-gray-900 dark:text-white">
              Comentários
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
              {article.title}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 min-h-[180px]">
          {loading ? (
            <div className="flex items-center justify-center py-12 gap-2 text-gray-400">
              <Loader2 size={18} className="animate-spin text-red-600" />
              <span className="text-sm">A carregar comentários…</span>
            </div>
          ) : approved.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Ainda sem comentários</p>
              <p className="text-xs text-gray-400 mt-1">Seja o primeiro a comentar esta notícia.</p>
            </div>
          ) : (
            approved.map((comment) => (
              <article
                key={comment._id}
                className={`rounded-xl border px-4 py-3 ${
                  comment.isPinned
                    ? 'border-amber-300/80 bg-amber-50/80 dark:border-amber-700/60 dark:bg-amber-950/30'
                    : 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${
                    comment.isPinned
                      ? 'bg-amber-500 text-white'
                      : 'bg-gray-900 text-white dark:bg-gray-700'
                  }`}>
                    {initials(comment.authorName)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                        {comment.authorName}
                      </p>
                      {comment.isPinned ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-400">
                          <Crown size={12} />
                          Fixado
                        </span>
                      ) : null}
                    </div>
                    <p className="text-[11px] text-gray-400 mt-0.5">{formatCommentDate(comment.createdAt)}</p>
                    <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed mt-2 whitespace-pre-wrap">
                      {comment.content}
                    </p>
                  </div>
                </div>
              </article>
            ))
          )}
        </div>

        <form onSubmit={handleSubmit} className="border-t border-gray-100 dark:border-gray-800 px-5 py-4 space-y-3 bg-white dark:bg-gray-900">
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-gray-400">
            Deixar um comentário
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nome"
              required
              maxLength={80}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500"
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="E-mail"
              required
              maxLength={120}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Escreva o seu comentário…"
            required
            rows={3}
            maxLength={800}
            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
          />
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] text-gray-400">
              O comentário só aparece depois de aprovado.
            </p>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-60 transition-colors"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Enviar
            </button>
          </div>
        </form>
      </div>
    </div>
  ) : null

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        aria-label={`${count} ${count === 1 ? 'comentário' : 'comentários'}. Abrir comentários`}
        className="comment-bubble absolute bottom-3 right-3 z-20 inline-flex items-center gap-2 rounded-full bg-blue-600 pl-3.5 pr-4 py-2.5 sm:py-3 text-white shadow-[0_10px_28px_rgba(220,38,38,0.45)] ring-2 ring-white/90 hover:bg-blue-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400"
      >
        <span className="comment-bubble-ring" aria-hidden="true" />
        <span className="comment-bubble-ring comment-bubble-ring-delay" aria-hidden="true" />
        <MessageCircle size={20} className="text-yellow-300 shrink-0 sm:w-6 sm:h-6" />
        <span className="flex flex-col items-start leading-none">
          <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.14em] text-yellow-200">
            Comentar
          </span>
          <span className="text-sm sm:text-base font-black tabular-nums mt-0.5">
            {count} {count === 1 ? 'comentário' : 'comentários'}
          </span>
        </span>
      </button>

      {typeof document !== 'undefined' && modal ? createPortal(modal, document.body) : null}
    </>
  )
}
